import { Fragment, memo, type PropsWithChildren, useEffect, useMemo } from 'react';

import { getAccountPlan } from '@/data/account/selectors';
import Country from '@/data/country/model';
import { useLDFlag } from '@/data/LD/selectors/getLDFlag';
import { fetchUserMetadataIfNeeded } from '@/data/metadata/actions';
import { ADD_PHONE_NUMBER_MODAL_LAST_INTERACTED_AT } from '@/data/metadata/keys';
import { getUserMetadata, getUserMetadataLoaded } from '@/data/metadata/selectors';
import { openDialog } from '@/dialogs';
import { ADD_PHONE_NUMBER_DIALOG } from '@/settings/dialogs';
import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import { useWiwDispatch, useWiwSelector } from '@/store';

import moment from 'moment-timezone';

const THIRTEEN_DAYS_MS = 13 * 24 * 60 * 60 * 1000;
const ONE_HOUR_MS = 60 * 60 * 1000;

const AddPhoneNumberGate = memo(function AddPhoneNumberGate({ children }: PropsWithChildren<any>) {
  const dispatch = useWiwDispatch();
  const account = useWiwSelector(getAuthAccount);
  const accountPlan = useWiwSelector(getAccountPlan);
  const authUser = useWiwSelector(getAuthUser);
  const userMetadata = useWiwSelector(getUserMetadata);
  const userMetadataLoaded = useWiwSelector(getUserMetadataLoaded);
  const addPhoneNumberModalEnabled = useLDFlag<boolean>('phone-number-collection-dialog');

  // Show dialog only when: LD flag on, user is admin, no phone set, billing address is US,
  // not on a trial plan, not within first hour of conversion,
  // and either never interacted with the modal or last interaction was 90+ days ago.
  const shouldShowDialog = useMemo(() => {
    if (!userMetadataLoaded) {
      return false;
    }
    if (!addPhoneNumberModalEnabled) {
      return false;
    }
    if (!authUser.isAdmin()) {
      return false;
    }
    const phone = (authUser.phone_number || '').trim();
    if (phone.length > 0) {
      return false;
    }
    // Only show for accounts with US billing address
    if (account.billing_country !== Country.UNITED_STATES) {
      return false;
    }
    if (accountPlan?.isTrial()) {
      return false;
    }
    // Don't show for first hour after conversion to paid
    if (account.converted_at && typeof account.converted_at === 'string') {
      const convertedAt = moment(account.converted_at);
      if (convertedAt.isValid() && Date.now() - convertedAt.valueOf() < ONE_HOUR_MS) {
        return false;
      }
    }

    const lastInteractedAt = userMetadata.get(ADD_PHONE_NUMBER_MODAL_LAST_INTERACTED_AT);
    if (!lastInteractedAt) {
      return true;
    }
    const at = typeof lastInteractedAt === 'string' ? moment(lastInteractedAt) : null;
    if (!at || !at.isValid()) {
      return true;
    }
    return Date.now() - at.valueOf() >= THIRTEEN_DAYS_MS;
  }, [
    userMetadataLoaded,
    addPhoneNumberModalEnabled,
    authUser,
    account.billing_country,
    account.converted_at,
    accountPlan,
    userMetadata,
  ]);

  useEffect(() => {
    dispatch(fetchUserMetadataIfNeeded());
  }, [dispatch]);

  useEffect(() => {
    if (shouldShowDialog) {
      dispatch(openDialog(ADD_PHONE_NUMBER_DIALOG));
    }
  }, [dispatch, shouldShowDialog]);

  return <Fragment>{children}</Fragment>;
});

export default AddPhoneNumberGate;
